/**
 * Firebase Analytics Core Service
 *
 * DEPRECATED: Firebase Analytics has been migrated to PostHog.
 * All Firebase Analytics calls are commented out, but functions remain
 * to avoid breaking existing code. Events now only go to PostHog.
 */

// Firebase Analytics imports commented out - migrated to PostHog
// import { getAnalytics, logEvent, setUserProperties, setUserId, Analytics } from 'firebase/analytics';
// import { app } from '@/config/firebase';
import { AnalyticsEventName, AnalyticsEventParams } from './events';
import { capturePostHogEvent, identifyPostHogUser, resetPostHog, setPostHogPersonProperties } from './posthog';
import { isE2ETestEnvironment } from './e2e';
import { trackMetaFromPostHogEvent } from './metaFunnel.client';

// Firebase Analytics disabled - using PostHog only
// let analytics: Analytics | null = null;
let isInitialized = false;
let isDisabled = false;

/**
 * Initialize Firebase Analytics
 * DEPRECATED: Firebase Analytics disabled - using PostHog only
 */
export const initializeAnalytics = (): null => {
  if (typeof window === 'undefined') {
    // Server-side rendering - analytics not available
    return null;
  }

  // Skip analytics initialization in E2E test environments
  if (isE2ETestEnvironment()) {
    isDisabled = true;
    return null;
  }

  // Firebase Analytics commented out - migrated to PostHog
  // if (!isInitialized) {
  //   try {
  //     analytics = getAnalytics(app);
  //     isInitialized = true;
  //   } catch (error) {
  //     console.error('❌ Failed to initialize Firebase Analytics:', error);
  //     return null;
  //   }
  // }

  return null;
};

/**
 * Get the analytics instance (lazy initialization)
 * DEPRECATED: Always returns null - Firebase Analytics disabled
 */
export const getAnalyticsInstance = (): null => {
  // Firebase Analytics disabled - using PostHog only
  return null;
};

/**
 * Track a custom event
 * Now only sends to PostHog (Firebase Analytics disabled)
 *
 * @param eventName - Name of the event to track
 * @param params - Optional parameters for the event
 */
export const trackEvent = (
  eventName: AnalyticsEventName,
  params?: AnalyticsEventParams
): void => {
  // Firebase Analytics disabled - event now only goes to PostHog
  // const analyticsInstance = getAnalyticsInstance();
  // if (!analyticsInstance) {
  //   return;
  // }

  try {
    // Clean params - remove undefined values
    const cleanParams = params ? cleanObject(params) : undefined;

    // Firebase Analytics commented out - migrated to PostHog
    // logEvent(analyticsInstance, eventName as string, cleanParams);

    // Send to PostHog only
    capturePostHogEvent(eventName as string, cleanParams);
    try {
      trackMetaFromPostHogEvent(eventName, cleanParams);
    } catch {
      // Meta mapping must not throw or change the PostHog payload.
    }
  } catch (error) {
    console.error('❌ Failed to track event:', eventName, error);
  }
};

/**
 * Set user ID for tracking
 * Now only sends to PostHog (Firebase Analytics disabled)
 *
 * @param userId - User's unique ID (Firebase Auth UID)
 */
export const setAnalyticsUserId = (userId: string | null): void => {
  // Firebase Analytics disabled
  // const analyticsInstance = getAnalyticsInstance();
  // if (!analyticsInstance) return;

  // try {
  //   setUserId(analyticsInstance, userId);
  // } catch (error) {
  //   console.error('❌ Failed to set user ID:', error);
  // }

  // PostHog only: identify merges anonymous events into the user profile;
  // null means logout → reset so the next visitor gets a fresh anonymous ID.
  if (userId) {
    identifyPostHogUser(userId);
  } else {
    resetPostHog();
  }
};

/**
 * Set user properties for segmentation
 * Now only sends to PostHog (Firebase Analytics disabled)
 *
 * @param properties - User properties to set
 */
export const setAnalyticsUserProperties = (properties: Record<string, any>): void => {
  // Firebase Analytics disabled
  // const analyticsInstance = getAnalyticsInstance();
  // if (!analyticsInstance) return;

  try {
    // Clean properties - remove undefined values
    const cleanProps = cleanObject(properties);

    // Firebase Analytics commented out - migrated to PostHog
    // setUserProperties(analyticsInstance, cleanProps);

    // Send to PostHog only
    setPostHogPersonProperties(cleanProps);
  } catch (error) {
    console.error('❌ Failed to set user properties:', error);
  }
};

/**
 * Track page view with custom parameters
 *
 * @param pageTitle - Title of the page
 * @param pagePath - Path of the page
 * @param additionalParams - Additional parameters
 */
export const trackPageView = (
  pageTitle: string,
  pagePath: string,
  additionalParams?: Record<string, any>
): void => {
  trackEvent('page_view', {
    page_title: pageTitle,
    page_path: pagePath,
    ...additionalParams,
  });
};

/**
 * Track errors with context
 *
 * @param errorType - Type/category of error
 * @param errorMessage - Error message
 * @param additionalContext - Additional context
 */
export const trackError = (
  errorType: string,
  errorMessage: string,
  additionalContext?: Record<string, any>
): void => {
  trackEvent('app_error', {
    error_type: errorType,
    error_message: errorMessage,
    page_url: typeof window !== 'undefined' ? window.location.href : undefined,
    ...additionalContext,
  });
};

/**
 * Track API errors
 *
 * @param endpoint - API endpoint that failed
 * @param statusCode - HTTP status code
 * @param errorMessage - Error message
 */
export const trackApiError = (
  endpoint: string,
  statusCode: number,
  errorMessage: string
): void => {
  trackEvent('api_error', {
    error_type: 'api_error',
    endpoint,
    status_code: statusCode,
    error_message: errorMessage,
  });
};

/**
 * Track Firebase/Firestore errors
 *
 * @param operation - Firestore operation (read, write, etc)
 * @param collection - Collection name
 * @param errorCode - Firebase error code
 * @param errorMessage - Error message
 */
export const trackFirestoreError = (
  operation: string,
  collection: string,
  errorCode: string,
  errorMessage: string
): void => {
  trackEvent('firestore_error', {
    error_type: 'firestore_error',
    operation_type: operation,
    collection_name: collection,
    error_code: errorCode,
    error_message: errorMessage,
  });
};

/**
 * Helper function to remove undefined values from objects
 * Firebase Analytics doesn't accept undefined values
 */
const cleanObject = (obj: Record<string, any>): Record<string, any> => {
  const cleaned: Record<string, any> = {};

  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    if (value !== undefined && value !== null) {
      cleaned[key] = value;
    }
  });

  return cleaned;
};

/**
 * Helper to calculate time difference in hours
 */
export const calculateHoursUntil = (futureDate: Date): number => {
  const now = new Date();
  const diffMs = futureDate.getTime() - now.getTime();
  return Math.round(diffMs / (1000 * 60 * 60));
};

/**
 * Helper to get email domain from email address
 */
export const getEmailDomain = (email: string): string => {
  const parts = email.split('@');
  return parts.length > 1 ? parts[1] : 'unknown';
};

/**
 * Helper to extract UTM parameters from URL
 */
export const getUtmParams = (): Record<string, string> => {
  if (typeof window === 'undefined') return {};

  const params = new URLSearchParams(window.location.search);
  const utmParams: Record<string, string> = {};

  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach((param) => {
    const value = params.get(param);
    if (value) {
      utmParams[param] = value;
    }
  });

  return utmParams;
};

export default {
  initializeAnalytics,
  trackEvent,
  trackPageView,
  trackError,
  trackApiError,
  trackFirestoreError,
  setAnalyticsUserId,
  setAnalyticsUserProperties,
};
